import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from 'context/useAuth';
import { useCollection } from 'functions/hooks/useCollection';

interface DeviceWithUptime {
  id: string;
  name: string;
  vehicleId?: string;
  vehicleName?: string; 
  status: 'Online' | 'Offline';
  lastEventTimestamp: Date | null;
  lastActive: string;
  firmwareVersion?: string;
  // Client information
  clientId?: string;
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
}

export default function AllUptime() {
  const { authorisedUser, firebaseAuth } = useAuth();
  void authorisedUser;
  void firebaseAuth;
  const [devices, setDevices] = useState<DeviceWithUptime[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);
  const [debugDevice, setDebugDevice] = useState<any>(null);
  // Raw device data for event display
  const [rawDevices, setRawDevices] = useState<any[]>([]);
  
  // Filter state
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [filteredDevices, setFilteredDevices] = useState<DeviceWithUptime[]>([]);
  const [sortedDevices, setSortedDevices] = useState<DeviceWithUptime[]>([]);
  
  // Selected device for event details
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [deviceEventData, setDeviceEventData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'lastEvent' | 'statusUpdate' | 'location' | 'battery'>('lastEvent');
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ascending' | 'descending' | null;
  }>({
    key: '',
    direction: null
  });
  
  // User metrics
  const [totalUsers, setTotalUsers] = useState(0);
  const [usersWithVehicles, setUsersWithVehicles] = useState(0);
  const [usersWithLinkedDevices, setUsersWithLinkedDevices] = useState(0);
  const [usersWithoutVehicles, setUsersWithoutVehicles] = useState(0);
  
  // Vehicle metrics
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [vehiclesWithDevices, setVehiclesWithDevices] = useState(0);
  const [vehiclesWithoutDevices, setVehiclesWithoutDevices] = useState(0);
  const [vehiclesNotLinkedToUser, setVehiclesNotLinkedToUser] = useState(0);
  
  // Device metrics
  const [totalDevices, setTotalDevices] = useState(0);
  const [activeDevices, setActiveDevices] = useState(0);
  
  // Offline threshold configuration
  const [offlineThresholdHours, setOfflineThresholdHours] = useState(() => {
    // Load saved threshold from local storage, or use default of 48 hours
    const savedThreshold = localStorage.getItem('offlineThresholdHours');
    return savedThreshold ? parseInt(savedThreshold) : 48;
  });
  const [showThresholdConfig, setShowThresholdConfig] = useState(false);

  // Firebase collections
  const devicesCollection = useCollection<any>('devices');
  const vehiclesCollection = useCollection<any>('vehicles');
  const usersCollection = useCollection<any>('users');

  // Drops stale refresh results so newer fetches always win.
  const cancelRef = useRef(0);

  // O(1) id -> raw device lookup used by every alert card and the details panel.
  // Previously each lookup was Array.prototype.find over thousands of docs, on every render.
  const rawDevicesById = useMemo(() => {
    const m = new Map<string, any>();
    for (const d of rawDevices) m.set(d.id, d);
    return m;
  }, [rawDevices]);

  const fetchDevicesData = useCallback(async () => {
    const requestId = ++cancelRef.current;
    try {
      setLoading(true);
      setError(null);

      // Get current time for comparison
      const now = new Date();
      setLastRefreshed(now);

      // Fetch the three collections in parallel (was sequential).
      const [vehicles, allDevices, users] = await Promise.all([
        vehiclesCollection.queryMany(),
        devicesCollection.queryMany(),
        usersCollection.queryMany(),
      ]);

      if (requestId !== cancelRef.current) return;

      // Store raw device data for event display
      setRawDevices(allDevices);

      // Set total users count
      setTotalUsers(users.length);
      setTotalVehicles(vehicles.length);
      setTotalDevices(allDevices.length);

      // Pre-index everything by id so the rest of this function is O(N), not O(N*M).
      const vehicleByDeviceId = new Map<string, any>();
      const usersWithVehicleIds = new Set<string>();
      let vehiclesNotLinkedCount = 0;
      for (const v of vehicles) {
        const uid = v.data?.userId;
        if (uid) usersWithVehicleIds.add(uid);
        else vehiclesNotLinkedCount += 1;
        const did = v.data?.deviceId;
        if (did) vehicleByDeviceId.set(did, v);
      }
      setUsersWithVehicles(usersWithVehicleIds.size);
      setUsersWithoutVehicles(users.length - usersWithVehicleIds.size);
      setVehiclesNotLinkedToUser(vehiclesNotLinkedCount);

      const deviceIds = new Set<string>();
      for (const d of allDevices) deviceIds.add(d.id);

      // Vehicles whose deviceId points to a real device doc.
      const vehiclesWithDevicesList: any[] = [];
      const usersWithLinkedDevicesSet = new Set<string>();
      for (const v of vehicles) {
        const did = v.data?.deviceId;
        if (did && deviceIds.has(did)) {
          vehiclesWithDevicesList.push(v);
          const uid = v.data?.userId;
          if (uid) usersWithLinkedDevicesSet.add(uid);
        }
      }
      setVehiclesWithDevices(vehiclesWithDevicesList.length);
      setVehiclesWithoutDevices(vehicles.length - vehiclesWithDevicesList.length);
      setUsersWithLinkedDevices(usersWithLinkedDevicesSet.size);

      if (allDevices.length > 0) {
        setDebugDevice(allDevices[0]);
      }

      // Devices that have an associated vehicle (active).
      const devicesWithVehicles = allDevices.filter(d => vehicleByDeviceId.has(d.id));
      setActiveDevices(devicesWithVehicles.length);

      // User lookup by id.
      const userById = new Map<string, any>();
      for (const u of users) userById.set(u.id, u);

      // Process devices to determine uptime status
      const processedDevices: DeviceWithUptime[] = devicesWithVehicles.map(device => {
        // Find associated vehicle for this device
        const associatedVehicle = vehicleByDeviceId.get(device.id);
        const vehicleDetails = associatedVehicle?.data.details || {};

        // Get client information if available
        const clientId = associatedVehicle?.data?.userId || '';
        const clientUser = clientId ? userById.get(clientId) : undefined;
        const clientProfile = clientUser?.data?.profile || {};
        
        // Check various timestamp fields that might indicate device activity
        let lastEventTime: Date | null = null;
        let timestampSources: Array<{source: string, timestamp: Date}> = [];
        
        // Check StatusUpdateLog timestamp
        if (device.data.StatusUpdateLog?.logTimestamp) {
          if (typeof device.data.StatusUpdateLog.logTimestamp === 'string') {
            const timestamp = new Date(device.data.StatusUpdateLog.logTimestamp);
            timestampSources.push({source: 'StatusUpdateLog', timestamp});
          } else if (device.data.StatusUpdateLog.logTimestamp.toDate) {
            const timestamp = device.data.StatusUpdateLog.logTimestamp.toDate();
            timestampSources.push({source: 'StatusUpdateLog', timestamp});
          }
        }
        
        // Check BatteryStatusLog timestamp
        if (device.data.BatteryStatusLog?.logTimestamp) {
          if (typeof device.data.BatteryStatusLog.logTimestamp === 'string') {
            const timestamp = new Date(device.data.BatteryStatusLog.logTimestamp);
            timestampSources.push({source: 'BatteryStatusLog', timestamp});
          } else if (device.data.BatteryStatusLog.logTimestamp.toDate) {
            const timestamp = device.data.BatteryStatusLog.logTimestamp.toDate();
            timestampSources.push({source: 'BatteryStatusLog', timestamp});
          }
        }
        
        // Check LockResultLog timestamp
        if (device.data.LockResultLog?.logTimestamp) {
          if (typeof device.data.LockResultLog.logTimestamp === 'string') {
            const timestamp = new Date(device.data.LockResultLog.logTimestamp);
            timestampSources.push({source: 'LockResultLog', timestamp});
          } else if (device.data.LockResultLog.logTimestamp.toDate) {
            const timestamp = device.data.LockResultLog.logTimestamp.toDate();
            timestampSources.push({source: 'LockResultLog', timestamp});
          }
        }
        
        // Check LastLocation timestamp
        if (device.data.LastLocation?.updatedAt) {
          if (typeof device.data.LastLocation.updatedAt === 'string') {
            const timestamp = new Date(device.data.LastLocation.updatedAt);
            timestampSources.push({source: 'LastLocation', timestamp});
          } else if (device.data.LastLocation.updatedAt.toDate) {
            const timestamp = device.data.LastLocation.updatedAt.toDate();
            timestampSources.push({source: 'LastLocation', timestamp});
          }
        }
        
        // Check lastMessageTimestamp
        if (device.data.lastMessageTimestamp) {
          if (typeof device.data.lastMessageTimestamp === 'string') {
            const timestamp = new Date(device.data.lastMessageTimestamp);
            timestampSources.push({source: 'lastMessageTimestamp', timestamp});
          } else if (device.data.lastMessageTimestamp.toDate) {
            const timestamp = device.data.lastMessageTimestamp.toDate();
            timestampSources.push({source: 'lastMessageTimestamp', timestamp});
          }
        }
        
        // If we have any timestamp sources, find the most recent one
        if (timestampSources.length > 0) {
          // O(N) max instead of O(N log N) sort — we only need the newest.
          let newest = timestampSources[0];
          for (let i = 1; i < timestampSources.length; i++) {
            if (timestampSources[i].timestamp.getTime() > newest.timestamp.getTime()) {
              newest = timestampSources[i];
            }
          }
          lastEventTime = newest.timestamp;
        }
        
        // Calculate status based on the most recent event time
        const isOffline = lastEventTime === null || 
          (now.getTime() - lastEventTime.getTime() > offlineThresholdHours * 60 * 60 * 1000);
        
        // Extract firmware version from device data
        let firmwareVersion = 'Unknown';
        if (device.data.firmwareInfo?.version) {
          firmwareVersion = device.data.firmwareInfo.version;
        } else if (device.data.DeviceInfo?.firmwareVersion) {
          firmwareVersion = device.data.DeviceInfo.firmwareVersion;
        } else if (device.data.StatusUpdateLog?.firmwareVersion) {
          firmwareVersion = device.data.StatusUpdateLog.firmwareVersion;
        } else if (device.data.currentFirmwareVersion) {
          const fv = device.data.currentFirmwareVersion;
          if (fv.majorVersion !== undefined && fv.minorVersion !== undefined && fv.patchVersion !== undefined) {
            firmwareVersion = `${fv.majorVersion}.${fv.minorVersion}.${fv.patchVersion}`;
          }
        } else if (device.data.StatusUpdateLog?.currentFirmwareVersion) {
          const fv = device.data.StatusUpdateLog.currentFirmwareVersion;
          if (fv.majorVersion !== undefined && fv.minorVersion !== undefined && fv.patchVersion !== undefined) {
            firmwareVersion = `${fv.majorVersion}.${fv.minorVersion}.${fv.patchVersion}`;
          }
        }
        
        return {
          id: device.id,
          name: vehicleDetails.registrationNumber || device.id.slice(-8),
          vehicleId: associatedVehicle?.id,
          vehicleName: vehicleDetails.make ? `${vehicleDetails.make} ${vehicleDetails.model || ''}` : 'Unknown Vehicle',
          status: isOffline ? 'Offline' : 'Online',
          lastEventTimestamp: lastEventTime,
          lastActive: lastEventTime ? formatLastActive(lastEventTime) : 'Unknown',
          firmwareVersion: firmwareVersion,
          // Add client information
          clientId: clientId,
          clientName: clientProfile.firstName && clientProfile.lastName 
            ? `${clientProfile.firstName} ${clientProfile.lastName}`
            : clientId ? `User ${clientId.substring(0, 8)}...` : 'Unknown',
          clientPhone: clientProfile.phoneNumber 
            ? `${clientProfile.phoneCode || ''} ${clientProfile.phoneNumber}` 
            : '',
          clientEmail: clientProfile.email || ''
        };
      });
      
      if (requestId !== cancelRef.current) return;
      setDevices(processedDevices);
      setFilteredDevices(processedDevices);
      setSortedDevices(processedDevices);

      if (processedDevices.length === 0) {
        setError('No devices linked to vehicles were found. This may indicate a configuration issue.');
      }

    } catch (error) {
      if (requestId !== cancelRef.current) return;
      console.error('Error fetching devices:', error);
      setError('Failed to load device data. Please try again later.');
    } finally {
      if (requestId === cancelRef.current) {
        setLoading(false);
      }
    }
  }, [vehiclesCollection, devicesCollection, usersCollection, offlineThresholdHours]);

  useEffect(() => {
    fetchDevicesData();
  }, [fetchDevicesData]);

  // Apply filters when devices or active filter changes
  useEffect(() => {
    if (!activeFilter) {
      setFilteredDevices(devices);
      setSortedDevices(devices);
    }
    // We don't need the rest of the switch statement here since 
    // handleCardClick now handles filtering directly
  }, [devices]); // Only depend on devices changing

  // Apply sorting to filtered devices
  useEffect(() => {
    let sortableDevices = [...filteredDevices];
    
    if (sortConfig.key && sortConfig.direction) {
      sortableDevices.sort((a, b) => {
        // Get the values to compare based on the sort key
        let aValue, bValue;
        
        switch (sortConfig.key) {
          case 'id':
            aValue = a.id;
            bValue = b.id;
            break;
          case 'client':
            aValue = a.clientName || '';
            bValue = b.clientName || '';
            break;
          case 'vehicle':
            aValue = a.vehicleName || '';
            bValue = b.vehicleName || '';
            break;
          case 'registration':
            aValue = a.name || '';
            bValue = b.name || '';
            break;
          case 'status':
            aValue = a.status;
            bValue = b.status;
            break;
          case 'firmware':
            aValue = a.firmwareVersion || '';
            bValue = b.firmwareVersion || '';
            break;
          case 'lastActive':
            // For last active, we need to compare actual timestamps
            if (a.lastEventTimestamp && b.lastEventTimestamp) {
              aValue = a.lastEventTimestamp.getTime();
              bValue = b.lastEventTimestamp.getTime();
            } else if (a.lastEventTimestamp) {
              return sortConfig.direction === 'ascending' ? -1 : 1;
            } else if (b.lastEventTimestamp) {
              return sortConfig.direction === 'ascending' ? 1 : -1;
            } else {
              return 0;
            }
            break;
          default:
            aValue = a.id;
            bValue = b.id;
        }
        
        // Perform the comparison
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setSortedDevices(sortableDevices);
  }, [filteredDevices, sortConfig]);

  const handleSort = (key: string) => {
    let direction: 'ascending' | 'descending' | null = 'ascending';
    
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'ascending') {
        direction = 'descending';
      } else if (sortConfig.direction === 'descending') {
        direction = null;
      }
    }
    
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) {
      return <span className="sort-icon">↕</span>;
    }
    
    return sortConfig.direction === 'ascending' 
      ? <span className="sort-icon asc">↑</span> 
      : <span className="sort-icon desc">↓</span>;
  };

  const handleRefresh = () => {
    fetchDevicesData();
  };

  const handleCardClick = (filterType: string) => {
    // If clicking the active filter, toggle it off
    if (activeFilter === filterType) {
      setActiveFilter(null);
      setFilteredDevices(devices); // Reset to all devices
      setSortedDevices(devices); // Reset sorted devices too
    } else {
      setActiveFilter(filterType);
      
      // Immediately apply the filter (same logic as in the useEffect)
      let filtered: DeviceWithUptime[] = [];
      
      switch (filterType) {
        // Device-related filters
        case 'totalDevices':
        case 'activeDevices':
          filtered = devices;
          break;
        case 'onlineDevices':
          filtered = devices.filter(device => device.status === 'Online');
          break;
        case 'offlineDevices':
          filtered = devices.filter(device => device.status === 'Offline');
          break;

        // Device status alert filters
        case 'lowBattery':
          filtered = devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              rawDevice?.data?.BatteryStatusLog?.internalBatteryVoltageMilli !== undefined && 
              rawDevice.data.BatteryStatusLog.internalBatteryVoltageMilli < 4000;
          });
          break;
        case 'lowVehicleBattery':
          filtered = devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              rawDevice?.data?.BatteryStatusLog?.vehicleBatteryVoltageMilli !== undefined && 
              rawDevice.data.BatteryStatusLog.vehicleBatteryVoltageMilli < 10800 &&
              rawDevice.data.BatteryStatusLog.vehicleBatteryVoltageMilli > 6000;
            });
          break;
        case 'lowRemoteBattery':
          filtered = devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              Object.values(rawDevice?.data?.Remotes?? new Object())
              ?.filter((r: any) =>
                (r.remoteSerialNumber?.serialNumber?.startsWith("50") ?? false) &&
                r.remoteBatteryVoltageMilli < 2300 &&
                r.remoteBatteryVoltageMilli > 1000)?.length > 0;
            });
          break;
        case 'lowPanicBattery':
          filtered = devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              Object.values(rawDevice?.data?.Remotes?? new Object())
              ?.filter((r: any) => (r.remoteSerialNumber?.serialNumber?.startsWith("60") ?? false) &&
              r.remoteBatteryVoltageMilli < 2300 &&
              r.remoteBatteryVoltageMilli > 1000)?.length > 0;
            });
          break;
          case 'autolockOff':
          filtered = devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              rawDevice?.data?.StatusUpdateLog?.autoLockPeriodSec === 0;
            });
          break;
        case 'movingIgnitionOff':
          filtered = devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            if (!rawDevice || !rawDevice.data || !rawDevice.data.StatusUpdateLog) return false;
            
            // Ensure both 'moving' and 'ignitionOn' properties exist and have the expected values
            const statusLog = rawDevice.data.StatusUpdateLog;
            const hasMovingProperty = Object.prototype.hasOwnProperty.call(statusLog, 'moving');
            
            return device.status === 'Online' && 
              hasMovingProperty && 
              statusLog.moving === true && 
              statusLog.ignitionOn === false;
          });
          break;
        case 'lockedMoving':
          filtered = devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            if (!rawDevice || !rawDevice.data || !rawDevice.data.StatusUpdateLog) return false;
            
            // Ensure 'moving' property exists and has the expected value
            const statusLog = rawDevice.data.StatusUpdateLog;
            const hasMovingProperty = Object.prototype.hasOwnProperty.call(statusLog, 'moving');
            
            return device.status === 'Online' && 
              hasMovingProperty && 
              statusLog.moving === true && 
              statusLog.currentState !== "SYSTEM_STATE_UNLOCKED" && 
              statusLog.currentState !== "0";
          });
          break;
        case 'batteryDisconnected':
          filtered = devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              rawDevice?.data?.StatusUpdateLog?.vehicleBatteryConnected === false;
          });
          break;

        // Offline analysis filters
        case 'recentlyOffline':
          filtered = devices.filter(d => 
            d.status === 'Offline' && 
            d.lastEventTimestamp && 
            new Date().getTime() - d.lastEventTimestamp.getTime() < 7 * 24 * 60 * 60 * 1000 &&
            new Date().getTime() - d.lastEventTimestamp.getTime() >= offlineThresholdHours * 60 * 60 * 1000
          );
          break;
        case 'extendedOffline':
          filtered = devices.filter(d => 
            d.status === 'Offline' && 
            d.lastEventTimestamp && 
            new Date().getTime() - d.lastEventTimestamp.getTime() >= 7 * 24 * 60 * 60 * 1000
          );
          break;
        case 'neverConnected':
          filtered = devices.filter(d => d.status === 'Offline' && !d.lastEventTimestamp);
          break;
        default:
          filtered = devices;
      }
      
      setFilteredDevices(filtered);
      setSortedDevices(filtered); // Update sorted devices immediately too
    }
    
    // Close any open device detail view when changing filters
    setSelectedDevice(null);
    setDeviceEventData(null);
  };

  const handleDeviceClick = (deviceId: string) => {
    // If clicking the already selected device, deselect it
    if (selectedDevice === deviceId) {
      setSelectedDevice(null);
      setDeviceEventData(null);
    } else {
      setSelectedDevice(deviceId);
      setActiveTab('lastEvent'); // Reset to first tab when selecting a new device
      // Find the device in the raw device data
      const rawDevice = rawDevicesById.get(deviceId);
      if (rawDevice) {
        // Extract the event data we want to show
        const eventData = {
          lastMessageTimestamp: rawDevice.data.lastMessageTimestamp,
          StatusUpdateLog: rawDevice.data.StatusUpdateLog,
          BatteryStatusLog: rawDevice.data.BatteryStatusLog,
          LockResultLog: rawDevice.data.LockResultLog,
          LastLocation: rawDevice.data.LastLocation
        };
        setDeviceEventData(eventData);
      } else {
        setDeviceEventData({ error: "Original device data not found" });
      }
    }
  };

  // Format the "Last Event" tab content
  const formatLastEventTab = (data: any) => {
    if (!data) return "No data available";
    
    // If there's an error message, just show that
    if (data.error) return data.error;
    
    // Format timestamp if it exists
    const formatTimestamp = (timestamp: any) => {
      if (!timestamp) return "N/A";
      
      if (typeof timestamp === 'string') {
        return new Date(timestamp).toLocaleString();
      } else if (timestamp.toDate) {
        return timestamp.toDate().toLocaleString();
      } else if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleString();
      }
      
      return JSON.stringify(timestamp);
    };
    
    // Find the most recent timestamp to determine the last event
    const timestamps = [];
    
    if (data.lastMessageTimestamp) {
      timestamps.push({
        source: 'Last Message',
        timestamp: data.lastMessageTimestamp,
        formatted: formatTimestamp(data.lastMessageTimestamp)
      });
    }
    
    if (data.StatusUpdateLog?.logTimestamp) {
      timestamps.push({
        source: 'Status Update',
        timestamp: data.StatusUpdateLog.logTimestamp,
        formatted: formatTimestamp(data.StatusUpdateLog.logTimestamp)
      });
    }
    
    if (data.BatteryStatusLog?.logTimestamp) {
      timestamps.push({
        source: 'Battery Status',
        timestamp: data.BatteryStatusLog.logTimestamp,
        formatted: formatTimestamp(data.BatteryStatusLog.logTimestamp)
      });
    }
    
    if (data.LockResultLog?.logTimestamp) {
      timestamps.push({
        source: 'Lock Result',
        timestamp: data.LockResultLog.logTimestamp,
        formatted: formatTimestamp(data.LockResultLog.logTimestamp)
      });
    }
    
    if (data.LastLocation?.updatedAt) {
      timestamps.push({
        source: 'Location Update',
        timestamp: data.LastLocation.updatedAt,
        formatted: formatTimestamp(data.LastLocation.updatedAt)
      });
    }
    
    // Sort timestamps by most recent first
    timestamps.sort((a, b) => {
      const getTime = (timestamp: any) => {
        if (typeof timestamp === 'string') {
          return new Date(timestamp).getTime();
        } else if (timestamp.toDate) {
          return timestamp.toDate().getTime();
        } else if (timestamp.seconds) {
          return timestamp.seconds * 1000;
        }
        return 0;
      };
      
      return getTime(b.timestamp) - getTime(a.timestamp);
    });
    
    return (
      <div className="tab-content">
        <div className="data-section">
          <h5>Last Communication</h5>
          {timestamps.length > 0 ? (
            <>
              <div className="data-row">
                <span className="data-label">Event Type:</span>
                <span className="data-value">{timestamps[0].source}</span>
              </div>
              <div className="data-row">
                <span className="data-label">Timestamp:</span>
                <span className="data-value">{timestamps[0].formatted}</span>
              </div>
              <div className="data-row">
                <span className="data-label">Time Elapsed:</span>
                <span className="data-value">
                  {timestamps[0].timestamp ? formatLastActive(new Date(timestamps[0].formatted)) : 'Unknown'}
                </span>
              </div>
            </>
          ) : (
            <div className="data-row">
              <span className="data-value">No communication events recorded</span>
            </div>
          )}
        </div>
        
        {timestamps.length > 1 && (
          <div className="data-section">
            <h5>Other Recent Events</h5>
            {timestamps.slice(1, 4).map((item, index) => (
              <div className="data-row" key={index}>
                <span className="data-label">{item.source}:</span>
                <span className="data-value">{item.formatted}</span>
              </div>
            ))}
          </div>
        )}
        
        <div className="data-section">
          <button className="view-raw-json" onClick={(e) => {
            e.stopPropagation();
            alert(JSON.stringify(data, null, 2));
          }}>
            View Raw JSON
          </button>
        </div>
      </div>
    );
  };
  
  // Format the "Status Update" tab content
  const formatStatusUpdateTab = (data: any) => {
    if (!data) return "No data available";
    
    // If there's an error message, just show that
    if (data.error) return data.error;
    
    // Format timestamp if it exists
    const formatTimestamp = (timestamp: any) => {
      if (!timestamp) return "N/A";
      
      if (typeof timestamp === 'string') {
        return new Date(timestamp).toLocaleString();
      } else if (timestamp.toDate) {
        return timestamp.toDate().toLocaleString();
      } else if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleString();
      } else if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleString();
      }
      
      return JSON.stringify(timestamp);
    };
    
    // Extract firmware version
    let firmwareVersion = 'Unknown';
    if (data.firmwareInfo?.version) {
      firmwareVersion = data.firmwareInfo.version;
    } else if (data.DeviceInfo?.firmwareVersion) {
      firmwareVersion = data.DeviceInfo.firmwareVersion;
    } else if (data.StatusUpdateLog?.firmwareVersion) {
      firmwareVersion = data.StatusUpdateLog.firmwareVersion;
    } else if (data.currentFirmwareVersion) {
      const fv = data.currentFirmwareVersion;
      if (fv.majorVersion !== undefined && fv.minorVersion !== undefined && fv.patchVersion !== undefined) {
        firmwareVersion = `${fv.majorVersion}.${fv.minorVersion}.${fv.patchVersion}`;
      }
    } else if (data.StatusUpdateLog?.currentFirmwareVersion) {
      const fv = data.StatusUpdateLog.currentFirmwareVersion;
      if (fv.majorVersion !== undefined && fv.minorVersion !== undefined && fv.patchVersion !== undefined) {
        firmwareVersion = `${fv.majorVersion}.${fv.minorVersion}.${fv.patchVersion}`;
      }
    }
    
    if (!data.StatusUpdateLog) {
      return (
        <div className="tab-content">
          <div className="data-section">
            <p className="no-data">No status update data available for this device.</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="tab-content">
        <div className="data-section">
          <h5>Timing Information</h5>
          <div className="data-row">
            <span className="data-label">Updated:</span>
            <span className="data-value">{formatTimestamp(data.StatusUpdateLog.logTimestamp)}</span>
          </div>
          {data.StatusUpdateLog.log && (
            <>
              <div className="data-row">
                <span className="data-label">Sequence #:</span>
                <span className="data-value">{data.StatusUpdateLog.log.sequenceNumber || 'N/A'}</span>
              </div>
              <div className="data-row">
                <span className="data-label">Timestamp:</span>
                <span className="data-value">{formatTimestamp(data.StatusUpdateLog.log.timestampEpochMilliSec)}</span>
              </div>
            </>
          )}
        </div>
        
        <div className="data-section">
          <h5>Device Information</h5>
          <div className="data-row">
            <span className="data-label">Firmware Version:</span>
            <span className="data-value">{firmwareVersion}</span>
          </div>
        </div>
        
        <div className="data-section">
          <h5>System Status</h5>
          <div className="data-row">
            <span className="data-label">Current State:</span>
            <span className="data-value" style={{ fontWeight: 'bold' }}>
              {data.StatusUpdateLog.currentState || "Unknown"}
            </span>
          </div>
          {data.StatusUpdateLog.autoLockPeriodSec !== undefined && (
            <div className="data-row">
              <span className="data-label">Auto-Lock Period:</span>
              <span className="data-value">{data.StatusUpdateLog.autoLockPeriodSec} seconds</span>
            </div>
          )}
        </div>
        
        <div className="data-section">
          <h5>Network Status</h5>
          <div className="data-row network-status">
            {data.StatusUpdateLog.lteConnected !== undefined && (
              <span className={`status-indicator ${data.StatusUpdateLog.lteConnected ? 'online' : 'offline'}`}>
                LTE: {data.StatusUpdateLog.lteConnected ? "Connected" : "Disconnected"}
              </span>
            )}
            {data.StatusUpdateLog.mqttConnected !== undefined && (
              <span className={`status-indicator ${data.StatusUpdateLog.mqttConnected ? 'online' : 'offline'}`}>
                MQTT: {data.StatusUpdateLog.mqttConnected ? "Connected" : "Disconnected"}
              </span>
            )}
          </div>
          
          {data.StatusUpdateLog.networkRssi !== undefined && (
            <div className="data-row">
              <span className="data-label">Network RSSI:</span>
              <span className="data-value">{data.StatusUpdateLog.networkRssi} dBm</span>
            </div>
          )}
          {data.StatusUpdateLog.simCcid && (
            <div className="data-row">
              <span className="data-label">SIM CCID:</span>
              <span className="data-value">{data.StatusUpdateLog.simCcid}</span>
            </div>
          )}
        </div>
        
        <div className="data-section">
          <h5>Vehicle Status</h5>
          <div className="data-row">
            <span className="data-label">Moving:</span>
            <span className="data-value">
              {Object.prototype.hasOwnProperty.call(data.StatusUpdateLog, 'moving') && data.StatusUpdateLog.moving === true 
                ? "Yes" 
                : "No"}
            </span>
          </div>
          <div className="data-row">
            <span className="data-label">Battery Connected:</span>
            <span className="data-value">{data.StatusUpdateLog.vehicleBatteryConnected ? "Yes" : "No"}</span>
          </div>
          {data.StatusUpdateLog.ignitionOn !== undefined && (
            <div className="data-row">
              <span className="data-label">Ignition:</span>
              <span className="data-value">{data.StatusUpdateLog.ignitionOn ? "On" : "Off"}</span>
            </div>
          )}
        </div>
        
        <div className="data-section">
          <button className="view-raw-json" onClick={(e) => {
            e.stopPropagation();
            alert(JSON.stringify({
              StatusUpdateLog: data.StatusUpdateLog
            }, null, 2));
          }}>
            View Raw Status JSON
          </button>
        </div>
      </div>
    );
  };
  
  // Format the "Location Update" tab content
  const formatLocationTab = (data: any) => {
    if (!data) return "No data available";
    
    // If there's an error message, just show that
    if (data.error) return data.error;
    
    // Format timestamp if it exists
    const formatTimestamp = (timestamp: any) => {
      if (!timestamp) return "N/A";
      
      if (typeof timestamp === 'string') {
        return new Date(timestamp).toLocaleString();
      } else if (timestamp.toDate) {
        return timestamp.toDate().toLocaleString();
      } else if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleString();
      }
      
      return JSON.stringify(timestamp);
    };
    
    if (!data.LastLocation) {
      return (
        <div className="tab-content">
          <div className="data-section">
            <p className="no-data">No location data available for this device.</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="tab-content">
        <div className="data-section">
          <h5>Location</h5>
          {data.LastLocation.latLng && (
            <>
              <div className="data-row">
                <span className="data-label">Coordinates:</span>
                <span className="data-value">
                  {data.LastLocation.latLng.latitudeDeg !== undefined && 
                   data.LastLocation.latLng.longitudeDeg !== undefined 
                    ? `${data.LastLocation.latLng.latitudeDeg}, ${data.LastLocation.latLng.longitudeDeg}` 
                    : "Unknown"}
                </span>
              </div>
              {data.LastLocation.latLng.altitudeMetre !== undefined && (
                <div className="data-row">
                  <span className="data-label">Altitude:</span>
                  <span className="data-value">{data.LastLocation.latLng.altitudeMetre} m</span>
                </div>
              )}
            </>
          )}
          <div className="data-row">
            <span className="data-label">Updated:</span>
            <span className="data-value">{formatTimestamp(data.LastLocation.updatedAt)}</span>
          </div>
        </div>
        
        <div className="data-section">
          <button className="view-raw-json" onClick={(e) => {
            e.stopPropagation();
            alert(JSON.stringify({
              LastLocation: data.LastLocation
            }, null, 2));
          }}>
            View Raw Location JSON
          </button>
        </div>
      </div>
    );
  };

  // Format the "Battery" tab content
  const formatBatteryTab = (data: any) => {
    if (!data) return "No data available";
    
    // If there's an error message, just show that
    if (data.error) return data.error;
    
    // Format timestamp if it exists
    const formatTimestamp = (timestamp: any) => {
      if (!timestamp) return "N/A";
      
      if (typeof timestamp === 'string') {
        return new Date(timestamp).toLocaleString();
      } else if (timestamp.toDate) {
        return timestamp.toDate().toLocaleString();
      } else if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleString();
      } else if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleString();
      }
      
      return JSON.stringify(timestamp);
    };
    
    // Get appropriate class for battery level
    const getBatteryLevelClass = (level: number) => {
      if (level >= 70) return "battery-level-high";
      if (level >= 30) return "battery-level-medium";
      return "battery-level-low";
    };
    
    if (!data.BatteryStatusLog) {
      return (
        <div className="tab-content">
          <div className="data-section">
            <p className="no-data">No battery data available for this device.</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="tab-content">
        <div className="data-section">
          <h5>Battery Information</h5>
          <div className="data-row">
            <span className="data-label">Updated:</span>
            <span className="data-value">{formatTimestamp(data.BatteryStatusLog.logTimestamp)}</span>
          </div>
          
          {data.BatteryStatusLog.log && (
            <>
              <div className="data-row">
                <span className="data-label">Sequence #:</span>
                <span className="data-value">{data.BatteryStatusLog.log.sequenceNumber || 'N/A'}</span>
              </div>
              <div className="data-row">
                <span className="data-label">Timestamp:</span>
                <span className="data-value">{formatTimestamp(data.BatteryStatusLog.log.timestampEpochMilliSec)}</span>
              </div>
            </>
          )}
        </div>
        
        <div className="data-section">
          <h5>Power Status</h5>
          {/* Vehicle battery status from either source */}
          {(data.BatteryStatusLog.vehicleBatteryConnected !== undefined || data.StatusUpdateLog?.vehicleBatteryConnected !== undefined) && (
            <div className="data-row">
              <span className="data-label">Vehicle Battery Connected:</span>
              <span className="data-value">
                {data.BatteryStatusLog.vehicleBatteryConnected !== undefined 
                  ? (data.BatteryStatusLog.vehicleBatteryConnected ? "Yes" : "No")
                  : (data.StatusUpdateLog?.vehicleBatteryConnected ? "Yes" : "No")}
              </span>
            </div>
          )}
          
          {/* Internal battery charging status */}
          {data.BatteryStatusLog.internalBatteryCharging !== undefined && (
            <div className="data-row">
              <span className="data-label">Internal Battery Charging:</span>
              <span className="data-value">{data.BatteryStatusLog.internalBatteryCharging ? "Yes" : "No"}</span>
            </div>
          )}
        </div>
        
        <div className="data-section">
          <h5>Battery Levels</h5>
          {/* Internal battery voltage in millivolts */}
          {data.BatteryStatusLog.internalBatteryVoltageMilli !== undefined && (
            <div className="data-row">
              <span className="data-label">Internal Battery Voltage:</span>
              <span className="data-value">{data.BatteryStatusLog.internalBatteryVoltageMilli} mV</span>
            </div>
          )}
          
          {data.BatteryStatusLog.voltage !== undefined && (
            <div className="data-row">
              <span className="data-label">Voltage:</span>
              <span className="data-value">{data.BatteryStatusLog.voltage} V</span>
            </div>
          )}
          
          {data.BatteryStatusLog.level !== undefined && (
            <div className="data-row">
              <span className="data-label">Level:</span>
              <span className={`data-value ${getBatteryLevelClass(data.BatteryStatusLog.level)}`}>
                {data.BatteryStatusLog.level}%
              </span>
            </div>
          )}
          
          {data.BatteryStatusLog.percentage !== undefined && (
            <div className="data-row">
              <span className="data-label">Percentage:</span>
              <span className={`data-value ${getBatteryLevelClass(data.BatteryStatusLog.percentage)}`}>
                {data.BatteryStatusLog.percentage}%
              </span>
            </div>
          )}
        </div>
        
        <div className="data-section">
          <button className="view-raw-json" onClick={(e) => {
            e.stopPropagation();
            alert(JSON.stringify({
              BatteryStatusLog: data.BatteryStatusLog
            }, null, 2));
          }}>
            View Raw Battery JSON
          </button>
        </div>
      </div>
    );
  };

  // Utility to get class for active filter card
  const getCardClass = (filterType: string) => {
    return activeFilter === filterType ? 'summary-card active' : 'summary-card';
  };

  const formatLastActive = (timestamp: Date | null): string => {
    if (!timestamp) return 'Unknown';
    
    const now = new Date();
    const diffMs = now.getTime() - timestamp.getTime();
    const diffMins = Math.floor(diffMs / (60 * 1000));
    const diffHours = Math.floor(diffMs / (60 * 60 * 1000));
    const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const formatLastRefreshed = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // Add this handler for saving threshold changes
  const handleThresholdChange = (hours: number) => {
    setOfflineThresholdHours(hours);
    localStorage.setItem('offlineThresholdHours', hours.toString());
  };

  if (loading && devices.length === 0) {
    return <div className="loading">Loading device data...</div>;
  }

  return (
    <div className="uptime-dashboard">
      {error && <div className="error-message">{error}</div>}
      
      <div className="dashboard-header">
        <div className="last-updated">
          Last updated: {formatLastRefreshed(lastRefreshed)}
        </div>
        <div className="header-actions">
          {activeFilter && (
            <button 
              className="clear-filter-button" 
              onClick={() => setActiveFilter(null)}
            >
              Clear Filter
            </button>
          )}
          <button 
            className="refresh-button" 
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
      </div>
      
      <h2 className="section-title">Devices</h2>
      <div className="uptime-summary">
        <div className={getCardClass('totalDevices')} onClick={() => handleCardClick('totalDevices')}>
          <h3>Total Devices</h3>
          <p className="value">{totalDevices}</p>
          <p className="description">All devices in system</p>
        </div>
        <div className={getCardClass('activeDevices')} onClick={() => handleCardClick('activeDevices')}>
          <h3>Active Devices</h3>
          <p className="value">{activeDevices}</p>
          <p className="description">Linked to vehicles</p>
        </div>
        <div className={getCardClass('onlineDevices')} onClick={() => handleCardClick('onlineDevices')}>
          <h3>Online Devices</h3>
          <p className="value">{devices.filter(device => device.status === 'Online').length}</p>
          <p className="description">Active in the last {offlineThresholdHours} {offlineThresholdHours === 1 ? 'hour' : 'hours'}</p>
        </div>
        <div className={getCardClass('offlineDevices')} onClick={() => handleCardClick('offlineDevices')}>
          <h3>Offline Devices</h3>
          <p className="value">{devices.filter(device => device.status === 'Offline').length}</p>
          <p className="description">No activity in {offlineThresholdHours}+ {offlineThresholdHours === 1 ? 'hour' : 'hours'}</p>
        </div>
      </div>
      
      <h2 className="section-title">Device Status Alerts</h2>
      <div className="uptime-summary">
        <div className={getCardClass('lowBattery')} onClick={() => handleCardClick('lowBattery')}>
          <h3>Low Battery</h3>
          <p className="value">{devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              rawDevice?.data?.BatteryStatusLog?.internalBatteryVoltageMilli !== undefined && 
              rawDevice.data.BatteryStatusLog.internalBatteryVoltageMilli < 4000;
          }).length}</p>
          <p className="description">Battery below 4000mV</p>
        </div>
        <div className={getCardClass('lowVehicleBattery')} onClick={() => handleCardClick('lowVehicleBattery')}>
          <h3>Low Vehicle Battery</h3>
          <p className="value">{devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              rawDevice?.data?.BatteryStatusLog?.vehicleBatteryVoltageMilli !== undefined && 
              rawDevice.data.BatteryStatusLog.vehicleBatteryVoltageMilli < 10800 &&
              rawDevice.data.BatteryStatusLog.vehicleBatteryVoltageMilli > 6000;
          }).length}</p>
          <p className="description">Vehicle Battery below 10.8V</p>
        </div>
        <div className={getCardClass('lowRemoteBattery')} onClick={() => handleCardClick('lowRemoteBattery')}>
          <h3>Low Remote Battery</h3>
          <p className="value">{devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              Object.values(rawDevice?.data?.Remotes?? new Object())?.filter((r: any) => 
                (r.remoteSerialNumber?.serialNumber?.startsWith("50") ?? false) &&
                r.remoteBatteryVoltageMilli < 2300 && r.remoteBatteryVoltageMilli > 1000)?.length > 0;
          }).length}</p>
          <p className="description">Remote battery(s) below 2.3V</p>
        </div>
        <div className={getCardClass('lowPanicBattery')} onClick={() => handleCardClick('lowPanicBattery')}>
          <h3>Low Panic Battery</h3>
          <p className="value">{devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              Object.values(rawDevice?.data?.Remotes?? new Object())?.filter((r: any) => 
                (r.remoteSerialNumber?.serialNumber?.startsWith("60") ?? false) &&
                r.remoteBatteryVoltageMilli < 2300 && r.remoteBatteryVoltageMilli > 1000)?.length > 0;
          }).length}</p>
          <p className="description">Remote battery(s) below 2.3V</p>
        </div>
        <div className={getCardClass('batteryDisconnected')} onClick={() => handleCardClick('batteryDisconnected')}>
          <h3>Battery Disconnected</h3>
          <p className="value">{devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              rawDevice?.data?.StatusUpdateLog?.vehicleBatteryConnected === false;
          }).length}</p>
          <p className="description">Vehicle battery disconnected</p>
        </div>
        <div className={getCardClass('autolockOff')} onClick={() => handleCardClick('autolockOff')}>
          <h3>Autolock Off</h3>
          <p className="value">{devices.filter(device => {
            const rawDevice = rawDevicesById.get(device.id);
            return device.status === 'Online' && 
              rawDevice?.data?.StatusUpdateLog?.autoLockPeriodSec === 0;
          }).length}</p>
          <p className="description">Autolock disabled</p>
        </div>
      </div>
      
      <div className="section-header-with-controls">
        <h2 className="section-title">Offline Devices Analysis</h2>
        <button 
          className="config-button"
          onClick={() => setShowThresholdConfig(!showThresholdConfig)}
        >
          ⚙️ Threshold Settings
        </button>
      </div>
      
      {showThresholdConfig && (
        <div className="threshold-config">
          <h3>Offline Threshold Configuration</h3>
          <div className="config-row">
            <label htmlFor="offlineThreshold">Offline Threshold (hours):</label>
            <input 
              type="range" 
              id="offlineThreshold" 
              min="1" 
              max="168" 
              step="1" 
              value={offlineThresholdHours}
              onChange={(e) => handleThresholdChange(parseInt(e.target.value))}
            />
            <span className="threshold-value">{offlineThresholdHours} hours</span>
          </div>
          <div className="threshold-description">
            <p>
              Devices will be considered offline when they haven't reported in the last {offlineThresholdHours} hours.
              {offlineThresholdHours === 24 && ' (1 day)'}
              {offlineThresholdHours === 48 && ' (2 days)'}
              {offlineThresholdHours === 72 && ' (3 days)'}
              {offlineThresholdHours === 168 && ' (1 week)'}
            </p>
          </div>
          <button 
            className="apply-button" 
            onClick={() => {
              fetchDevicesData();
              setShowThresholdConfig(false);
            }}
          >
            Apply Changes
          </button>
        </div>
      )}
      
      <div className="uptime-summary">
        <div className={getCardClass('recentlyOffline')} onClick={() => handleCardClick('recentlyOffline')}>
          <h3>Recently Offline</h3>
          <p className="value">{devices.filter(d => d.status === 'Offline' && d.lastEventTimestamp && new Date().getTime() - d.lastEventTimestamp.getTime() < 7 * 24 * 60 * 60 * 1000 && new Date().getTime() - d.lastEventTimestamp.getTime() >= offlineThresholdHours * 60 * 60 * 1000).length}</p>
          <p className="description">Offline between {offlineThresholdHours}h and 7d</p>
        </div>
        <div className={getCardClass('extendedOffline')} onClick={() => handleCardClick('extendedOffline')}>
          <h3>Extended Offline</h3>
          <p className="value">{devices.filter(d => d.status === 'Offline' && d.lastEventTimestamp && new Date().getTime() - d.lastEventTimestamp.getTime() >= 7 * 24 * 60 * 60 * 1000).length}</p>
          <p className="description">Offline for 7+ days</p>
        </div>
        <div className={getCardClass('neverConnected')} onClick={() => handleCardClick('neverConnected')}>
          <h3>Never Connected</h3>
          <p className="value">{devices.filter(d => d.status === 'Offline' && !d.lastEventTimestamp).length}</p>
          <p className="description">No recorded activity</p>
        </div>
      </div>

      <div className="unit-list">
        <h2 className="section-title">
          Unit Status 
          {activeFilter && (
            <span className="filter-indicator">
              {activeFilter === 'onlineDevices' ? 'Online Devices Only' : 
               activeFilter === 'offlineDevices' ? 'Offline Devices Only' :
               activeFilter === 'recentlyOffline' ? 'Recently Offline' :
               activeFilter === 'extendedOffline' ? 'Extended Offline' :
               activeFilter === 'neverConnected' ? 'Never Connected' :
               activeFilter === 'lowBattery' ? 'Low Battery Devices' :
               activeFilter === 'movingIgnitionOff' ? 'Moving with Ignition Off' :
               activeFilter === 'lockedMoving' ? 'Locked While Moving' :
               activeFilter === 'batteryDisconnected' ? 'Battery Disconnected' :
               `Filtered by ${activeFilter}`}
              ({sortedDevices.length} results)
            </span>
          )}
        </h2>
        {sortedDevices.length === 0 ? (
          <p className="no-data">{activeFilter ? 'No devices match the selected filter.' : 'No devices found that are linked to vehicles.'}</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('id')} className="sortable">
                  Device ID {getSortIcon('id')}
                </th>
                <th onClick={() => handleSort('client')} className="sortable">
                  Client {getSortIcon('client')}
                </th>
                <th>Contact</th>
                <th onClick={() => handleSort('vehicle')} className="sortable">
                  Vehicle {getSortIcon('vehicle')}
                </th>
                <th onClick={() => handleSort('registration')} className="sortable">
                  Registration {getSortIcon('registration')}
                </th>
                <th onClick={() => handleSort('status')} className="sortable">
                  Status {getSortIcon('status')}
                </th>
                <th onClick={() => handleSort('firmware')} className="sortable">
                  Firmware {getSortIcon('firmware')}
                </th>
                <th onClick={() => handleSort('lastActive')} className="sortable">
                  Last Active {getSortIcon('lastActive')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedDevices.map(device => (
                <>
                  <tr 
                    key={`row-${device.id}`} 
                    className={`${device.status === 'Offline' ? 'offline' : ''} ${selectedDevice === device.id ? 'selected' : ''}`}
                    onClick={() => handleDeviceClick(device.id)}
                  >
                    <td>{device.id}</td>
                    <td>{device.clientName}</td>
                    <td>
                      {device.clientEmail && 
                        <div className="contact-info">
                          <div className="email">{device.clientEmail}</div>
                          {device.clientPhone && <div className="phone">{device.clientPhone}</div>}
                        </div>
                      }
                    </td>
                    <td>{device.vehicleName}</td>
                    <td>{device.name}</td>
                    <td>
                      <span className={`status-indicator ${device.status.toLowerCase()}`}>
                        {device.status}
                      </span>
                    </td>
                    <td>{device.firmwareVersion}</td>
                    <td>{device.lastActive}</td>
                  </tr>
                  {selectedDevice === device.id && deviceEventData && (
                    <tr key={`details-${device.id}`} className="device-details-row">
                      <td colSpan={8}>
                        <div className="device-events-dropdown">
                          <div className="tabs-header">
                            <button 
                              className={`tab-button ${activeTab === 'lastEvent' ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab('lastEvent');
                              }}
                            >
                              Last Event
                            </button>
                            <button 
                              className={`tab-button ${activeTab === 'statusUpdate' ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab('statusUpdate');
                              }}
                            >
                              Status Update
                            </button>
                            <button 
                              className={`tab-button ${activeTab === 'location' ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab('location');
                              }}
                            >
                              Location
                            </button>
                            <button 
                              className={`tab-button ${activeTab === 'battery' ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab('battery');
                              }}
                            >
                              Battery
                            </button>
                            <button className="close-button" onClick={(e) => { 
                              e.stopPropagation();
                              setSelectedDevice(null); 
                              setDeviceEventData(null); 
                            }}>
                              Close
                            </button>
                          </div>
                          
                          <div className="tab-content-container">
                            {activeTab === 'lastEvent' && formatLastEventTab(deviceEventData)}
                            {activeTab === 'statusUpdate' && formatStatusUpdateTab(deviceEventData)}
                            {activeTab === 'location' && formatLocationTab(deviceEventData)}
                            {activeTab === 'battery' && formatBatteryTab(deviceEventData)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Commenting out debug device display since we now have a better UI for it */}
      {/*debugDevice && (
        <div className="debug-device" style={{marginTop: '2rem', display: 'none'}}>
          <h3>Debug: Device Structure</h3>
          <pre style={{fontSize: '10px', whiteSpace: 'pre-wrap', overflowX: 'auto', maxHeight: '300px', overflowY: 'auto'}}>
            {JSON.stringify(debugDevice, null, 2)}
          </pre>
        </div>
      )*/}
    </div>
  );
} 